import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { notificationApi } from '../api/notificationApi'
import type { TPatchNotificationSettingsPayload } from '../api/types/patchNotificationSettings'
import { getNotificationSettingsQueryOptions } from './useNotificationSettingsQuery'

const editNotificationSettingsOptions = <SettingsKey extends keyof TPatchNotificationSettingsPayload>({
  queryClient,
  settingsKey,
  updateParametersToken,
}: {
  queryClient: QueryClient | null
  settingsKey: SettingsKey | SettingsKey[]
  updateParametersToken?: string
}) => ({
  mutationFn: async (payload: TPatchNotificationSettingsPayload[SettingsKey]) => {
    if (typeof settingsKey === 'string') {
      return notificationApi.patchNotificationSettings({
        [settingsKey]: payload,
        ...(updateParametersToken && { updateParametersToken }),
      })
    } else {
      const newPayload = settingsKey.reduce<Partial<Record<SettingsKey, typeof payload>>>(
        (previousValues, currentValue) => {
          previousValues[currentValue] = payload
          return previousValues
        },
        {},
      )
      return notificationApi.patchNotificationSettings({
        ...newPayload,
        ...(updateParametersToken && { updateParametersToken }),
      })
    }
  },
  onSuccess: () => {
    queryClient?.invalidateQueries({ queryKey: [getNotificationSettingsQueryOptions().queryKey[0]] })
  },
  mutationKey: ['me_edit_notification_settings', settingsKey],
})

export const useNotificationSettingsMutation = <SettingsKey extends keyof TPatchNotificationSettingsPayload>(
  settingsKey: SettingsKey | SettingsKey[],
  updateParametersToken?: string,
) => {
  const queryClient = useQueryClient()

  return useMutation(editNotificationSettingsOptions<SettingsKey>({ queryClient, settingsKey, updateParametersToken }))
}
